import React, { useState, useRef, useEffect } from 'react';
import Icon from '../AppIcon';

const Select = ({
  label,
  description,
  error,
  options = [],
  value,
  onChange,
  placeholder = "Select an option",
  disabled = false,
  required = false,
  loading = false,
  multiple = false,
  searchable = false,
  clearable = false,
  id,
  name,
  className = ""
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const selectRef = useRef(null);
  const searchInputRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (selectRef?.current && !selectRef?.current?.contains(event?.target)) {
        setIsOpen(false);
        setSearchTerm('');
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      if (searchable && searchInputRef?.current) {
        searchInputRef?.current?.focus();
      }
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, searchable]);

  const filteredOptions = searchable
    ? options?.filter(option =>
        option?.label?.toLowerCase()?.includes(searchTerm?.toLowerCase())
      )
    : options;

  const selectedOption = options?.find(opt => opt?.value === value);
  const selectedOptions = multiple && Array.isArray(value)
    ? options?.filter(opt => value?.includes(opt?.value))
    : [];

  const handleSelect = (optionValue) => {
    if (multiple) {
      const newValue = Array.isArray(value) ? [...value] : [];
      const index = newValue?.indexOf(optionValue);
      if (index > -1) {
        newValue?.splice(index, 1);
      } else {
        newValue?.push(optionValue);
      }
      onChange(newValue);
    } else {
      onChange(optionValue);
      setIsOpen(false);
      setSearchTerm('');
    }
  };

  const handleClear = (e) => {
    e?.stopPropagation();
    onChange(multiple ? [] : '');
  };

  const displayValue = () => {
    if (multiple && selectedOptions?.length > 0) {
      return selectedOptions?.map(opt => opt?.label)?.join(', ');
    }
    return selectedOption ? selectedOption?.label : placeholder;
  };

  const hasValue = multiple ? (Array.isArray(value) && value?.length > 0) : value;

  return (
    <div className={className}>
      {label && (
        <label className="block font-caption text-sm font-medium text-foreground mb-2">
          {label}
          {required && <span className="text-error ml-1">*</span>}
        </label>
      )}
      {description && (
        <p className="font-caption text-xs text-muted-foreground mb-2">
          {description}
        </p>
      )}
      <div ref={selectRef} className="relative">
        <button
          type="button"
          onClick={() => !disabled && !loading && setIsOpen(!isOpen)}
          disabled={disabled || loading}
          className={`w-full px-4 py-3 bg-input border-2 rounded-md font-body text-sm text-left transition-smooth focus:outline-none focus-ring ${
            error
              ? 'border-error'
              : isOpen
              ? 'border-primary' :'border-border hover:border-primary/50'
          } ${
            disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'
          } ${
            !hasValue ? 'text-muted-foreground' : 'text-foreground'
          }`}
          aria-label={label}
          aria-expanded={isOpen}
          aria-required={required}
        >
          <div className="flex items-center justify-between">
            <span className="truncate">{displayValue()}</span>
            <div className="flex items-center space-x-2 flex-shrink-0 ml-2">
              {loading && (
                <Icon name="Loader2" size={16} strokeWidth={2} className="animate-spin" />
              )}
              {clearable && hasValue && !disabled && !loading && (
                <button
                  onClick={handleClear}
                  className="hover:text-error transition-smooth"
                  aria-label="Clear selection"
                >
                  <Icon name="X" size={16} strokeWidth={2} />
                </button>
              )}
              <Icon
                name="ChevronDown"
                size={16}
                strokeWidth={2}
                className={`transition-smooth ${isOpen ? 'rotate-180' : ''}`}
              />
            </div>
          </div>
        </button>

        {isOpen && (
          <div className="absolute z-dropdown w-full mt-2 bg-popover border border-border rounded-md shadow-elevation-4 max-h-60 overflow-hidden">
            {searchable && (
              <div className="p-2 border-b border-border">
                <div className="relative">
                  <Icon
                    name="Search"
                    size={16}
                    strokeWidth={2}
                    className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground"
                  />
                  <input
                    ref={searchInputRef}
                    type="text"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e?.target?.value)}
                    placeholder="Search..."
                    className="w-full pl-9 pr-3 py-2 bg-input border border-border rounded-md font-body text-sm focus:outline-none focus:border-primary transition-smooth"
                  />
                </div>
              </div>
            )}

            <div className="overflow-y-auto max-h-48">
              {filteredOptions?.length === 0 ? (
                <div className="px-4 py-3 text-center font-body text-sm text-muted-foreground">
                  No options found
                </div>
              ) : (
                filteredOptions?.map((option) => {
                  const isSelected = multiple
                    ? Array.isArray(value) && value?.includes(option?.value)
                    : value === option?.value;

                  return (
                    <button
                      key={option?.value}
                      type="button"
                      onClick={() => !option?.disabled && handleSelect(option?.value)}
                      disabled={option?.disabled}
                      className={`w-full px-4 py-3 text-left font-body text-sm transition-smooth ${
                        option?.disabled
                          ? 'opacity-50 cursor-not-allowed' :'hover:bg-muted cursor-pointer'
                      } ${
                        isSelected ? 'bg-primary/10 text-primary' : 'text-foreground'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex-1 min-w-0">
                          <p className="truncate">{option?.label}</p>
                          {option?.description && (
                            <p className="font-caption text-xs text-muted-foreground truncate mt-1">
                              {option?.description}
                            </p>
                          )}
                        </div>
                        {multiple && isSelected && (
                          <Icon name="Check" size={16} color="var(--color-primary)" strokeWidth={2.5} className="ml-2 flex-shrink-0" />
                        )}
                      </div>
                    </button>
                  );
                })
              )}
            </div>
          </div>
        )}
      </div>
      {error && (
        <p className="mt-2 font-caption text-xs text-error flex items-center space-x-1">
          <Icon name="AlertCircle" size={12} strokeWidth={2} />
          <span>{error}</span>
        </p>
      )}
      <input
        type="hidden"
        id={id}
        name={name}
        value={multiple ? JSON.stringify(value) : value}
      />
    </div>
  );
};

export default Select;